import { MessageBar, MessageBarType, PrimaryButton, Stack, TextField } from '@fluentui/react';
import React, { FC, useState } from 'react';
import { Footer } from '../../components/Footer';
import { Header } from '../../components/Header';
import { Types } from '../../config/Types';
import { IResponseError } from '../../interfaces/IResponseError';
import { ITokenObject } from '../../interfaces/ITokenObject';
import { Container } from '../../services/Container';
import { UserService } from '../../services/UserService';
import { ErrorHelper } from '../../utils/ErrorHelper';
import { getClassNames } from './resetpassword.style';

export interface IResetPassword {
    onGoToLoginPage: () => void;
}

export const ResetPassword: FC<IResetPassword> = ({ onGoToLoginPage }) => {
    const classNames = getClassNames();

    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<'email' | keyof ITokenObject, string>>>({});
    const [msg, setMsg] = useState('');

    const [tokenObject, setTokenObject] = useState<ITokenObject>({ token: '', newPassword: '', repeatNewPassword: '' });

    const goToLoginPage = () => {
        onGoToLoginPage();
    };

    const requestResetPassword = async () => {
        setFieldErrors({});
        try {
            const userService = Container.get(Types.userService) as UserService;
            await userService.requestResetPassword(email);
            setMsg(
                'Bitte prüfe dein Emailpostfach, eine Email mit einem Reset-Token wurde an dich erfolgreich gesendet. Bitte gebe den Token hier ein und setze dein Passwort zurück.'
            );
        } catch (e) {
            const errors = ErrorHelper.getFieldErrors(e as IResponseError);
            const mappedFieldErrors = {
                email: typeof errors.email === 'string' ? errors.email : undefined
            };

            setFieldErrors(mappedFieldErrors);

            if (typeof errors.global === 'string') {
                setError(errors.global);
                return;
            }

            if (Object.values(mappedFieldErrors).some((value) => Boolean(value))) {
                setError('');
                return;
            }

            setError(ErrorHelper.getMessage(e as IResponseError));
        }
    };

    const checkTokenObject = () => {
        const localFieldErrors: Partial<Record<keyof ITokenObject, string>> = {};

        if (!tokenObject.token) {
            localFieldErrors.token = 'Reset-Token wurde nicht gefunden';
        }

        if (!tokenObject.newPassword) {
            localFieldErrors.newPassword = 'Neues Passwort darf nicht leer sein';
        }

        if (!tokenObject.repeatNewPassword) {
            localFieldErrors.repeatNewPassword = 'Wiederholtes Passwort darf nicht leer sein';
        }

        if (tokenObject.newPassword !== tokenObject.repeatNewPassword) {
            localFieldErrors.repeatNewPassword = 'Wiederholtes passwort stimmt nicht mit dem neuen Passwort überein';
        }

        return localFieldErrors;
    };

    const resetPassword = async () => {
        setFieldErrors({});
        try {
            const localFieldErrors = checkTokenObject();
            if (Object.keys(localFieldErrors).length > 0) {
                setFieldErrors(localFieldErrors);
                return;
            }

            const userService = Container.get(Types.userService) as UserService;
            await userService.resetPassword(tokenObject);
            goToLoginPage();
        } catch (e) {
            const errors = ErrorHelper.getFieldErrors(e as IResponseError);
            const mappedFieldErrors = {
                token: typeof errors.token === 'string' ? errors.token : undefined,
                newPassword:
                    typeof errors.newPassword === 'string'
                        ? errors.newPassword
                        : typeof errors.password === 'string'
                          ? errors.password
                          : undefined,
                repeatNewPassword: typeof errors.repeatNewPassword === 'string' ? errors.repeatNewPassword : undefined
            };

            setFieldErrors(mappedFieldErrors);

            if (typeof errors.global === 'string') {
                setError(errors.global);
                return;
            }

            if (Object.values(mappedFieldErrors).some((value) => Boolean(value))) {
                setError('');
                return;
            }

            setError(ErrorHelper.getMessage(e as IResponseError));
        }
    };

    const onResetPassword = async (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        setError('');
        setFieldErrors({});
        if (msg) {
            resetPassword();
            return;
        }
        requestResetPassword();
    };

    const onEmailChanged = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setEmail(newValue as string);
    };

    const onTokenObjectChanged = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, v?: string) => {
        setTokenObject({ ...tokenObject, [e.currentTarget.name]: v || '' });
    };

    return (
        <>
            <Header>
                <h3>myGarage</h3>
            </Header>
            <div className={classNames.content}>
                <form className={classNames.form} onSubmit={onResetPassword}>
                    <h3>Reset password</h3>
                    {!msg && (
                        <>
                            <TextField
                                name="email"
                                placeholder="Deine email"
                                type="email"
                                required={true}
                                className={classNames.emailField}
                                value={email}
                                onChange={onEmailChanged}
                                errorMessage={fieldErrors.email}
                            />
                            <Stack horizontal={true} horizontalAlign="end">
                                <PrimaryButton type="submit">Reset password</PrimaryButton>
                            </Stack>
                        </>
                    )}
                    {msg && (
                        <>
                            <MessageBar className={classNames.successAlert} messageBarType={MessageBarType.success}>
                                {msg}
                            </MessageBar>
                            <TextField
                                name="token"
                                placeholder="Reset token aus der Email"
                                value={tokenObject.token}
                                onChange={(e, v) => onTokenObjectChanged(e, v)}
                                errorMessage={fieldErrors.token}
                            />
                            <TextField
                                name="newPassword"
                                type="password"
                                placeholder="Ein neuer Passwort"
                                value={tokenObject.newPassword}
                                onChange={(e, v) => onTokenObjectChanged(e, v)}
                                errorMessage={fieldErrors.newPassword}
                            />
                            <TextField
                                name="repeatNewPassword"
                                type="password"
                                placeholder="Wiederhole das neue password"
                                value={tokenObject.repeatNewPassword}
                                onChange={(e, v) => onTokenObjectChanged(e, v)}
                                errorMessage={fieldErrors.repeatNewPassword}
                            />
                            <Stack horizontal={true} horizontalAlign="end">
                                <PrimaryButton type="submit">Reset password</PrimaryButton>
                            </Stack>
                        </>
                    )}
                    {error && (
                        <MessageBar className={classNames.errorAlert} messageBarType={MessageBarType.error}>
                            {error}
                        </MessageBar>
                    )}
                    <div className={classNames.linkPanel}>
                        <a href="#!" className={classNames.loginLink} onClick={goToLoginPage}>
                            Einloggen
                        </a>
                    </div>
                </form>
            </div>
            <Footer />
        </>
    );
};
